import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '../api';
import { sessionKey } from '../auth/session';
import { AuthLayout } from './AuthLayout';
import { Banner, Spinner } from '../components/ui';
import { errorMessage } from '../api/problems';

type Outcome = 'confirming' | 'confirmed' | 'failed';

/**
 * Confirms an address from the emailed link.
 *
 * The link is a GET and confirming is a state change, so the page POSTs on arrival rather than the
 * API exposing a GET that mutates.
 *
 * The outcome is held in component state rather than in a `useMutation`, which is the obvious
 * choice and the wrong one here: this is the app's only write fired from a mount effect. React
 * StrictMode invokes that effect twice in development. The ref guard correctly stops a second
 * POST, but the re-subscribed mutation observer then never learns the result of the first — so the
 * page sat on "Confirming" forever while the address had in fact been confirmed.
 *
 * It reproduced only under the dev server, because a production build does not double-invoke. That
 * is why the end-to-end test, which drives this very screen against the built SPA on the API's own
 * port, has always passed. Plain state belongs to the component instance and survives the double
 * effect, so the answer always lands.
 */
export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const userId = params.get('userId') ?? '';
  const token = params.get('token') ?? '';

  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome>('confirming');
  const [failure, setFailure] = useState<unknown>(null);

  // One POST per visit, whatever the effect does.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || !userId || !token) {
      return;
    }

    attempted.current = true;

    void (async () => {
      const { error } = await auth.confirmEmail({ body: { userId, token } });

      if (error) {
        setFailure(error);
        setOutcome('failed');
        return;
      }

      setOutcome('confirmed');

      // The session carries a now-stale "unconfirmed" claim; refetch so writing becomes possible
      // without signing in again.
      void queryClient.invalidateQueries({ queryKey: sessionKey });
    })();
  }, [userId, token, queryClient]);

  if (!userId || !token) {
    return (
      <AuthLayout title="Broken link">
        <Banner tone="error">
          That confirmation link is missing information. Ask for a new one from the sign-in page.
        </Banner>
        <Link to="/login" className="text-sm text-accent hover:underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Confirming your address">
      {outcome === 'confirming' && <Spinner label="Confirming" />}

      {outcome === 'confirmed' && (
        <>
          <Banner tone="success">
            Your address is confirmed. You can start saving snippets.
          </Banner>
          <Link to="/" className="text-sm text-accent hover:underline">
            Open my drawer
          </Link>
        </>
      )}

      {outcome === 'failed' && (
        <>
          <Banner tone="error">
            {errorMessage(failure, 'That confirmation link is invalid or has expired.')}
          </Banner>
          <Link to="/login" className="text-sm text-accent hover:underline">
            Back to sign in
          </Link>
        </>
      )}
    </AuthLayout>
  );
}
